import {AgentsPlugin} from '@github-ui/agent-sessions/agents-plugin'
import {TasksPlugin} from '@github-ui/agent-sessions/tasks-plugin'
import type {HandleUserSubmit} from '@github-ui/agent-sessions/types/user-submit'
import type {ImmersivePlugin} from '@github-ui/copilot-chat/plugin'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {SparkPlugin} from '@github-ui/spark'
import type {RefObject} from 'react'
import {useMemo} from 'react'

export interface UseImmersivePluginsPayload {
  sparkEnabled?: boolean
  agentsEnabled?: boolean
  dashboardModulesEnabled?: boolean
}

export interface UseImmersivePluginsOptions {
  payload: UseImmersivePluginsPayload
  handleUserSubmitRef?: RefObject<HandleUserSubmit>
  isEmbedded?: boolean
}

export function useImmersivePlugins({
  payload,
  handleUserSubmitRef,
  isEmbedded,
}: UseImmersivePluginsOptions): ImmersivePlugin[] {
  const showSparkEmptyState = isFeatureEnabled('copilot_spark_empty_state')

  const plugins = useMemo(() => {
    const pluginsList: ImmersivePlugin[] = []

    if (payload.sparkEnabled || showSparkEmptyState) {
      pluginsList.push(new SparkPlugin(payload.sparkEnabled))
    }

    const shouldCreateTaskFromList = payload.dashboardModulesEnabled ?? true

    if (!isEmbedded) {
      pluginsList.push(new AgentsPlugin(shouldCreateTaskFromList, handleUserSubmitRef, payload.agentsEnabled ?? false))
    }

    // Tasks plugin is now always enabled
    pluginsList.push(new TasksPlugin())

    return pluginsList
  }, [
    payload.sparkEnabled,
    payload.agentsEnabled,
    payload.dashboardModulesEnabled,
    showSparkEmptyState,
    isEmbedded,
    handleUserSubmitRef,
  ])

  return plugins
}
